'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const DEMO_USERS = [
  { username: 'admin', password: 'admin123', role: 'Admin' },
  { username: 'teknisi1', password: 'teknisi123', role: 'Teknisi' },
  { username: 'cs1', password: 'cs123', role: 'CS' },
];

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(username, password);
      router.replace('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Terjadi kesalahan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (u: typeof DEMO_USERS[0]) => {
    setUsername(u.username);
    setPassword(u.password);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
          {/* Header with logo */}
          <div className="bg-primary/5 border-b border-border px-8 py-8 flex flex-col items-center gap-4">
            <div className="flex items-center justify-center">
              <Image
                src="/assets/images/winet-logo-1779243577654.png"
                alt="Winet Logo"
                width={160}
                height={60}
                className="object-contain"
                priority
              />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-700 text-foreground">eOffice</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Sistem Manajemen Operasional ISP</p>
            </div>
          </div>

          {/* Form */}
          <div className="px-8 py-7">
            <h2 className="text-base font-600 text-foreground mb-5">Masuk ke Akun Anda</h2>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                <Icon name="ExclamationCircleIcon" size={16} variant="outline" className="flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="admin"
                  className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 pr-10 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-70 active:scale-[0.98] transition-all mt-2"
              >
                {loading ? (
                  <><Icon name="ArrowPathIcon" size={15} variant="outline" className="animate-spin" /> Memproses...</>
                ) : (
                  <><Icon name="ArrowRightOnRectangleIcon" size={15} variant="outline" /> Masuk</>
                )}
              </button>
            </form>

            {/* Demo credentials */}
            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-xs font-600 text-muted-foreground mb-2">Akun Demo:</p>
              <div className="flex flex-wrap gap-2">
                {DEMO_USERS.map((u) => (
                  <button
                    key={u.username}
                    type="button"
                    onClick={() => fillDemo(u)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      u.role === 'Admin' ? 'bg-red-500' : u.role === 'Teknisi' ? 'bg-blue-500' : 'bg-green-500'
                    }`} />
                    <span className="font-600 text-foreground">{u.username}</span>
                    <span className="text-muted-foreground">({u.role})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-5">
          © 2026 Winet — eOffice Platform
        </p>
      </div>
    </div>
  );
}
